import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, FileText, Sparkles, Users, Settings, Shield,
  LogOut, Zap, Crown, ChevronRight, BarChart3, Clock, DollarSign, Calendar, Inbox, Network,
  User, Bell, Lock, HelpCircle, Camera, CreditCard, Building2, Globe, ListTodo, Activity, Play, FolderOpen
} from 'lucide-react';
import { auth } from '../utils/auth';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useDarkMode } from '../context/DarkModeContext';
import { useTranslation, LANGUAGES } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface SidebarProps {
  activeView: string;
  onNavigate: (view: string) => void;
  isExpanded: boolean;
  onToggle: () => void;
  onShowDemo?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeView, onNavigate, isExpanded, onToggle, onShowDemo }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const { t, language, setLanguage, currentLanguage } = useTranslation();
  const [user, setUser] = useState<any>(auth.getUser());
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Listen for user updates from localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      const updatedUser = auth.getUser();
      setUser(updatedUser);
    };

    // Listen for custom event when user data changes
    window.addEventListener('userUpdated', handleStorageChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('userUpdated', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  // Handle profile picture upload
  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Vennligst last opp et bilde');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bildet er for stort (maks 5MB)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = auth.getToken();
      const response = await axios.post(`${API}/auth/profile-picture`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update user data with full backend URL for profile picture
      const profilePictureUrl = response.data.profile_picture_url;
      const fullUrl = profilePictureUrl.startsWith('http') 
        ? profilePictureUrl 
        : `${process.env.REACT_APP_BACKEND_URL}${profilePictureUrl}`;
      
      const updatedUser = { ...user, profile_picture_url: fullUrl };
      setUser(updatedUser);
      auth.saveUser(updatedUser);
      
      toast.success('Profilbilde oppdatert!');
    } catch (error) {
      console.error('Profile picture upload error:', error);
      toast.error('Kunne ikke laste opp bilde');
    } finally {
      setUploading(false);
    }
  };

  const [expandedMenus, setExpandedMenus] = useState({
    'ai-workspace': true // AI Workspace er expanded by default
  });

  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  interface MenuItem {
    id: string;
    icon: any;
    label: string;
    hasNew?: boolean;
    hasSubmenu?: boolean;
    submenu?: Array<{ id: string; icon: any; label: string }>;
    group?: string;
  }

  // Define menu groups
  const menuGroups = {
    work: { label: language === 'en' ? 'WORK' : 'ARBEID', order: 1 },
    cases: { label: language === 'en' ? 'CASES' : 'SAKER', order: 2 },
    admin: { label: language === 'en' ? 'ADMIN' : 'ADMIN', order: 3 },
    extra: { label: language === 'en' ? 'MORE' : 'MER', order: 4 },
  };

  const menuItems: MenuItem[] = [
    // Work group - daily operations
    { id: 'overview', icon: Home, label: t('sidebar.overview') || t('common:sidebar.dashboard') || 'Overview', group: 'work' },
    { id: 'inbox', icon: Inbox, label: t('sidebar.inbox') || t('common:sidebar.inbox') || 'Inbox', group: 'work' },
    { id: 'case-queue', icon: ListTodo, label: t('common:sidebar.caseQueue') || (language === 'en' ? 'Work Queue' : 'Arbeidskø'), group: 'work' },
    { id: 'ai-workspace', icon: Sparkles, label: t('sidebar.aiWorkspace') || 'AI Workspace', group: 'work' },
    
    // Cases group - portfolio/context (Cases first, then Clients, then Documents, then Calendar)
    { id: 'cases', icon: FileText, label: t('sidebar.cases') || t('common:sidebar.cases') || 'Cases', group: 'cases' },
    { id: 'clients', icon: Users, label: t('sidebar.clients') || t('common:sidebar.clients') || 'Clients', group: 'cases' },
    { id: 'documents', icon: FolderOpen, label: language === 'en' ? 'Documents' : 'Dokumenter', group: 'cases' },
    { id: 'calendar', icon: Calendar, label: t('sidebar.calendar') || t('common:sidebar.calendar') || 'Calendar', group: 'cases' },
    
    // Admin group - money, reports & activity
    { id: 'time', icon: Clock, label: t('sidebar.timeTracking') || (language === 'en' ? 'Time Tracking' : 'Timeføring'), group: 'admin' },
    { id: 'invoices', icon: DollarSign, label: t('sidebar.invoicing') || t('common:sidebar.billing') || (language === 'en' ? 'Invoicing' : 'Fakturering'), group: 'admin' },
    { id: 'analytics', icon: BarChart3, label: t('sidebar.statistics') || (language === 'en' ? 'Analytics' : 'Analyse'), group: 'admin' },
    { id: 'activity-feed', icon: Activity, label: t('common:sidebar.activityFeed') || (language === 'en' ? 'Activity' : 'Aktivitet'), group: 'admin' },
    
    // Extra - community features
    { id: 'community', icon: Network, label: t('sidebar.community') || 'Community', hasNew: true, group: 'extra' },
    { id: 'marketplace', icon: Zap, label: t('sidebar.lawyerForAll') || (language === 'en' ? 'Lawyer for All' : 'Advokat for alle'), group: 'extra' },
  ];

  // Group the menu items
  const groupedMenuItems = menuItems.reduce((acc, item) => {
    const group = item.group || 'work';
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {} as Record<string, MenuItem[]>);

  return (
    <div className={`h-screen bg-[#0a0a0a] flex flex-col fixed left-0 top-0 z-30 transition-all duration-300 ease-out ${
      isExpanded ? 'w-56' : 'w-14'
    } md:flex hidden`}
    style={{
      willChange: 'width',
      transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      {/* Logo - Lexy Violet (no shimmer) */}
      <div className="h-14 flex items-center justify-between px-3 border-b border-white/[0.06]">
        <div className="w-8 h-8 flex-shrink-0">
          <div className="w-8 h-8 bg-gradient-to-r from-violet-600 to-purple-600 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105">
            <Zap className="w-4 h-4 text-white" />
          </div>
        </div>
        <div className={`flex-1 ml-3 overflow-hidden transition-all duration-300 ease-out ${isExpanded ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0'}`}>
          <h1 className="font-medium text-[15px] text-white/90 whitespace-nowrap tracking-tight">Lexy</h1>
        </div>
      </div>

      {/* Navigation - Pro OS style */}
      <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
        {Object.entries(menuGroups).map(([groupKey, groupConfig], groupIndex) => {
          const items = groupedMenuItems[groupKey];
          if (!items || items.length === 0) return null;
          
          // Extra spacing before "MER/MORE" section
          const isExtraGroup = groupKey === 'extra';
          
          return (
            <div key={groupKey} className={`${groupIndex > 0 ? 'mt-6' : ''} ${isExtraGroup ? 'mt-10' : ''}`}>
              {/* Group label - smaller, more subtle, more spacing */}
              {isExpanded && (
                <div className="px-4 pt-2 pb-2.5">
                  <span className="text-[10px] font-medium text-white/15 uppercase tracking-[0.12em]">
                    {groupConfig.label}
                  </span>
                </div>
              )}
              
              {/* Group items */}
              {items.map((item, index) => {
                const Icon = item.icon;
                const isActive = activeView === item.id || activeView?.startsWith(item.id + '-');
                const isMenuExpanded = expandedMenus[item.id];
                
                return (
                  <div key={item.id} 
                    className="transition-all duration-200 ease-out"
                    style={{ 
                      transitionDelay: `${index * 15}ms`,
                      opacity: 1,
                      transform: 'translateX(0)'
                    }}
                  >
                    <div className="relative group px-2 mb-0.5">
                      <button
                        onClick={() => {
                          if (item.hasSubmenu) {
                            toggleMenu(item.id);
                          }
                          onNavigate && onNavigate(item.id);
                        }}
                        className={`w-full h-9 flex items-center rounded-[4px] transition-all duration-150 ${
                          isExpanded ? 'justify-start px-2.5 gap-3' : 'justify-center'
                        } ${
                          isActive
                            ? 'bg-white/[0.08] text-white border-l-2 border-white/50 -ml-0.5 pl-2.5'
                            : 'text-white/45 hover:text-white/80 hover:bg-white/[0.06]'
                        }`}
                        title={!isExpanded ? item.label : undefined}
                      >
                        <div className="relative flex-shrink-0">
                          <Icon className={`w-[17px] h-[17px] transition-colors duration-150 ${isActive ? 'text-white/90' : 'text-white/40 group-hover:text-white/70'}`} strokeWidth={1.5} />
                          {item.hasNew && !isActive && (
                            <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-violet-400/80 rounded-full"></div>
                          )}
                        </div>
                        <div className={`overflow-hidden transition-all duration-300 ease-out ${isExpanded ? 'opacity-100 max-w-[180px]' : 'opacity-0 max-w-0'}`}>
                          <span className={`text-[13px] text-left whitespace-nowrap ${isActive ? 'font-medium' : 'font-normal'}`}>{item.label}</span>
                        </div>
                        {item.hasSubmenu && isExpanded && (
                          <svg 
                            className={`w-3 h-3 ml-auto opacity-25 transition-transform duration-200 ${isMenuExpanded ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </button>
                      
                      {/* Tooltip for collapsed state */}
                      {!isExpanded && (
                        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2.5 py-1.5 bg-white text-gray-900 text-xs font-medium rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-150 whitespace-nowrap z-50 shadow-lg">
                          {item.label}
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-white rotate-45"></div>
                        </div>
                      )}
                    </div>

                    {/* Submenu */}
                    {item.hasSubmenu && isExpanded && isMenuExpanded && item.submenu && (
                      <div className="ml-8 mr-2 mb-1 transition-all duration-200 ease-out">
                        {item.submenu.map((subitem) => {
                          const SubIcon = subitem.icon;
                          const isSubActive = activeView === subitem.id;
                          
                          return (
                            <button
                              key={subitem.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                onNavigate && onNavigate(subitem.id);
                              }}
                              className={`w-full h-8 flex items-center gap-2 px-2.5 rounded text-[13px] transition-all duration-150 mb-0.5 ${
                                isSubActive
                                  ? 'bg-white/[0.05] text-white/75 font-medium'
                                  : 'text-white/30 hover:text-white/55 hover:bg-white/[0.02]'
                              }`}
                            >
                              <SubIcon className="w-4 h-4" strokeWidth={1.5} />
                              <span className="text-left">{subitem.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Organisation Link - Only for TENANT_ADMIN, LAWYER and ADMIN */}
      {(user?.role === 'TENANT_ADMIN' || user?.role === 'ADMIN' || user?.role === 'LAWYER') && (
        <div className="px-2 py-2 border-t border-white/[0.06]">
          <div className="relative group">
            <button
              onClick={() => navigate('/admin/tenant')}
              className={`w-full h-9 flex items-center rounded-md transition-all duration-150 ${
                isExpanded ? 'justify-start px-2.5 gap-3' : 'justify-center'
              } text-white/40 hover:bg-white/[0.03] hover:text-white/70`}
              title={!isExpanded ? t('sidebar.organization') : undefined}
            >
              <Building2 className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
              {isExpanded && <span className="text-[13px] text-left tracking-wide">{t('sidebar.organization')}</span>}
            </button>
            
            {!isExpanded && (
              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2.5 py-1.5 bg-white text-gray-900 text-xs font-medium rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50">
                {t('sidebar.organization')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toggle button */}
      <div className="px-2 py-2">
        <button
          onClick={onToggle}
          className="w-full h-8 flex items-center justify-center rounded-md transition-all duration-150 text-white/25 hover:bg-white/[0.03] hover:text-white/50"
        >
          <ChevronRight className={`w-4 h-4 transition-transform duration-300 ease-out ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* User Profile - Quiet Style */}
      <div className="border-t border-white/[0.06] p-2">
        <div className="relative" ref={profileMenuRef}>
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className={`w-full h-10 flex items-center rounded-md transition-all duration-150 ${
              isExpanded ? 'justify-start px-2 gap-2.5' : 'justify-center'
            } ${
              showProfileMenu 
                ? 'bg-white/[0.08]' 
                : 'hover:bg-white/[0.03]'
            }`}
            title={!isExpanded ? user?.name : undefined}
          >
            <div className="relative flex-shrink-0">
              {user?.profile_picture_url ? (
                <img 
                  src={user.profile_picture_url} 
                  alt={user?.name || 'User'}
                  className="w-7 h-7 rounded-md object-cover ring-1 ring-white/10 transition-all duration-200"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    if (target.nextSibling instanceof HTMLElement) {
                      target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div 
                className="w-7 h-7 bg-white/10 rounded-md flex items-center justify-center text-white/60 font-medium text-xs ring-1 ring-white/[0.06]"
                style={{ display: user?.profile_picture_url ? 'none' : 'flex' }}
              >
                {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500/80 rounded-full border-[1.5px] border-[#0a0a0a]"></div>
            </div>
            <div className={`overflow-hidden transition-all duration-300 ease-out ${isExpanded ? 'opacity-100 max-w-full flex-1 min-w-0' : 'opacity-0 max-w-0'}`}>
              <div className="text-left">
                <p className="text-[13px] font-medium text-white/80 truncate">{user?.name}</p>
                <p className="text-[11px] text-white/35 truncate">Online</p>
              </div>
            </div>
          </button>

          {/* Profile Menu Dropdown - Quiet Style */}
          {showProfileMenu && (
            <div className={`absolute ${isExpanded ? 'left-0 right-0 bottom-full mb-2' : 'left-full ml-2 bottom-0'} bg-[#141414] border border-white/[0.08] rounded-lg shadow-2xl overflow-hidden z-50 min-w-[200px]`}>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePictureUpload}
                className="hidden"
              />

              {/* Menu Items - Direct start without duplicate user header */}
              <div className="py-1">
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    onNavigate && onNavigate('settings');
                  }}
                  className="w-full px-3 py-2 flex items-center gap-3 text-left transition-colors text-[13px] text-white/50 hover:bg-white/[0.04] hover:text-white/75"
                >
                  <User className="w-4 h-4 text-white/35" strokeWidth={1.5} />
                  <span>My Profile</span>
                </button>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    onNavigate && onNavigate('settings');
                  }}
                  className="w-full px-3 py-2 flex items-center gap-3 text-left transition-colors text-[13px] text-white/50 hover:bg-white/[0.04] hover:text-white/75"
                >
                  <Settings className="w-4 h-4 text-white/35" strokeWidth={1.5} />
                  <span>Settings</span>
                </button>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    onNavigate && onNavigate('settings');
                  }}
                  className="w-full px-3 py-2 flex items-center gap-3 text-left transition-colors text-[13px] text-white/50 hover:bg-white/[0.04] hover:text-white/75"
                >
                  <Bell className="w-4 h-4 text-white/35" strokeWidth={1.5} />
                  <span>Notifications</span>
                </button>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    navigate('/settings/billing');
                  }}
                  className="w-full px-3 py-2 flex items-center gap-3 text-left transition-colors text-[13px] text-white/50 hover:bg-white/[0.04] hover:text-white/75"
                  data-testid="billing-menu-item"
                >
                  <CreditCard className="w-4 h-4 text-white/35" strokeWidth={1.5} />
                  <span>Billing</span>
                </button>
                
                {/* Demo button */}
                {onShowDemo && (
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      onShowDemo();
                    }}
                    className="w-full px-3 py-2 flex items-center gap-3 text-left transition-colors text-[13px] text-white/50 hover:bg-white/[0.04] hover:text-white/75"
                  >
                    <Play className="w-4 h-4 text-white/35" strokeWidth={1.5} />
                    <span>Demo</span>
                  </button>
                )}
                
                {/* Language Selector */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLanguageMenu(!showLanguageMenu);
                    }}
                    className="w-full px-3 py-2 flex items-center gap-3 text-left transition-colors text-[13px] text-white/50 hover:bg-white/[0.04] hover:text-white/75"
                  >
                    <Globe className="w-4 h-4 text-white/35" strokeWidth={1.5} />
                    <span className="flex-1">Language</span>
                    <span className="text-sm opacity-70">{currentLanguage.flag}</span>
                  </button>
                  
                  {/* Language submenu */}
                  {showLanguageMenu && (
                    <div className="ml-7 mr-2 mb-1 rounded overflow-hidden bg-white/[0.03]">
                      {Object.values(LANGUAGES).map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setLanguage(lang.code);
                            setShowLanguageMenu(false);
                          }}
                          className={`w-full px-3 py-1.5 flex items-center gap-2 text-left transition-colors text-[12px] ${
                            language === lang.code 
                              ? 'bg-white/[0.06] text-white/70' 
                              : 'text-white/45 hover:bg-white/[0.04] hover:text-white/65'
                          }`}
                        >
                          <span className="text-sm">{lang.flag}</span>
                          <span>{lang.name}</span>
                          {language === lang.code && (
                            <span className="ml-auto text-[10px] text-white/50">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-white/[0.06] py-1">
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    // Navigate to help/support
                  }}
                  className="w-full px-3 py-2 flex items-center gap-3 text-left transition-colors text-[13px] text-white/50 hover:bg-white/[0.04] hover:text-white/75"
                >
                  <HelpCircle className="w-4 h-4 text-white/35" strokeWidth={1.5} />
                  <span>Help & Support</span>
                </button>
                <button
                  onClick={() => {
                    auth.logout();
                    navigate('/');
                  }}
                  className="w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-red-500/10 transition-colors text-[13px] text-red-400/70 hover:text-red-400"
                >
                  <LogOut className="w-4 h-4" strokeWidth={1.5} />
                  <span>Log out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
